import { AuthStorage } from "utils/auth-storage";
import React, { useState, useEffect } from "react";
import { Upload, Modal, Tooltip, Image } from "antd";
import {
  EyeOutlined,
  DeleteOutlined,
  EditOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import ImgCrop from "antd-img-crop";
import { CompanyAdminServices } from "apis/Services/CompanyAdminService";

const LogoUploader = ({ company_id }) => {
  const [fileList, setFileList] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [hasExistingLogo, setHasExistingLogo] = useState(false);

  const fetchLogo = async () => {
    const code = AuthStorage.getItem("token");
    const loginEmail = AuthStorage.getItem("email");

    try {
      const res = await CompanyAdminServices.getCompanyLogo({
        company_id,
        loginEmail,
        code,
      });

      let imgURL;
      if (res instanceof Blob) {
        imgURL = URL.createObjectURL(res);
      }

      setFileList([
        {
          uid: "-1",
          name: "company-logo.png",
          status: "done",
          url: imgURL,
        },
      ]);
      setHasExistingLogo(true);
    } catch (err) {
      console.error("Fetching logo failed:", err);
      setHasExistingLogo(false);
    }
  };

  const DeleteLogo = async () => {
    const code = AuthStorage.getItem("token");
    const loginEmail = AuthStorage.getItem("email");
    try {
      await CompanyAdminServices.deleteCompanyLogo({
        company_id,
        loginEmail,
        code,
      });
      setHasExistingLogo(false);
    } catch (err) {
      console.error("Deleting logo failed:", err);
      setHasExistingLogo(true);
    }
  };

  useEffect(() => {
    fetchLogo();
  }, [company_id]);

  const onChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file.originFileObj);
        reader.onload = () => resolve(reader.result);
      });
    }
    setPreviewImage(file.url || file.preview);
    setPreviewVisible(true);
    setPreviewTitle(file.name || "Logo Preview");
  };

  const customUploadRequest = async (options) => {
    const { file, onSuccess, onError } = options;
    const code = AuthStorage.getItem("token");
    const loginEmail = AuthStorage.getItem("email");

    const formData = new FormData();
    formData.append("logo", file);

    try {
      let res;
      if (hasExistingLogo) {
        res = await CompanyAdminServices.updateCompanyLogo({
          company: company_id,
          loginEmail,
          code,
          formData,
        });
      } else {
        res = await CompanyAdminServices.uploadCompanyLogo({
          company: company_id,
          loginEmail,
          code,
          formData,
        });
      }

      if (res.ok) {
        onSuccess("ok");
        console.log("Upload successful!");
        setHasExistingLogo(true); // Set to true after successful upload
        fetchLogo();
      } else {
        const errorText = await res.text();
        onError(new Error(errorText));
        console.error("Upload failed:", errorText);
      }
    } catch (err) {
      onError(err);
      console.error("Error uploading file:", err);
    }
  };

  return (
    <div>
      <div className="d-flex flex-column gap-1 mb-2">
        <h5 className="fw-bold">Upload Company Logo</h5>
        <small className="opacity-75">Recommended image size: 200x70px</small>
        <small className="opacity-75">Maximum file size: 3MB</small>
      </div>

      <ImgCrop rotationSlider aspect={200 / 70} quality={1}>
        <Upload
          customRequest={customUploadRequest}
          listType="picture-card"
          fileList={fileList}
          onChange={onChange}
          onPreview={handlePreview}
          accept=".png,.jpg,.jpeg"
          maxCount={1}
          onRemove={async () => {
            try {
              await DeleteLogo();
              setFileList([]);
              return true;
            } catch (err) {
              console.error("Delete failed:", err);
              return false;
            }
          }}
          beforeUpload={(file) => {
            const isJpgOrPng =
              file.type === "image/jpeg" || file.type === "image/png";
            if (!isJpgOrPng) {
              window.alert("You can only upload JPG/PNG files!");
              return Upload.LIST_IGNORE;
            }
            const isLt3M = file.size / 1024 / 1024 < 3;
            if (!isLt3M) {
              window.alert("Image must be smaller than 3MB!");
              return Upload.LIST_IGNORE;
            }
            return true;
          }}
          showUploadList={{
            showPreviewIcon: "false",
          }}
          // style={{
          //   width: "200px",
          //   height: "70px",
          // }}
          // className="logo-uploader"
        >
          {fileList.length < 1 ? (
            "+ Upload"
          ) : (
            <div>
              <EditOutlined /> Update
            </div>
          )}
        </Upload>
      </ImgCrop>

      {previewImage && (
        <Image
          wrapperStyle={{ display: "none" }}
          preview={{
            visible: previewVisible,
            onVisibleChange: (visible) => setPreviewVisible(visible),
            afterOpenChange: (visible) => !visible && setPreviewImage(""),
            toolbarRender: (
              originalNode,
              {
                transform: { scale },
                actions: { onZoomOut, onZoomIn, onReset },
              }
            ) => {
              const filteredIcons = originalNode.props.children.filter(
                (child) => {
                  if (!child || !child.key) return true;

                  return ![
                    "flipY",
                    "flipX",
                    "rotateLeft",
                    "rotateRight",
                    "zoomIn",
                    "zoomOut",
                  ].includes(child.key);
                }
              );

              return React.cloneElement(originalNode, {
                ...originalNode.props,
                children: filteredIcons,
              });
            },
          }}
          src={previewImage}
        />
      )}

      <style jsx>{`
        .logo-uploader .ant-upload-select {
          width: 200px !important;
          height: 70px !important;
        }
        .logo-uploader .ant-upload-list-picture-card-container {
          width: 200px !important;
          height: 70px !important;
        }
        .logo-uploader .ant-upload-list-picture-card .ant-upload-list-item {
          width: 200px !important;
          height: 70px !important;
        }
      `}</style>
    </div>
  );
};

export default LogoUploader;
